import React, { useState, useEffect, useCallback } from "react";
import "./App.css";
import SearchBar from "./components/SearchBar";
import Results from "./components/Results";
import SearchOverlay from "./components/SearchOverlay";

const categorize = (url) => {
  if (!url) return "All";
  const lower = url.toLowerCase();
  try {
    const u = new URL(url);
    if (u.pathname === "/" || u.pathname === "") return "Home";
  } catch {}
  if (lower.includes("/home")) return "Home";
  if (lower.includes("/about")) return "About";
  if (lower.includes("/contact")) return "Contact";
  if (lower.includes("/our-business") || lower.includes("/business"))
    return "Our Business";
  if (lower.includes("/blog") || lower.includes("/news")) return "Blog";
  if (
    lower.includes("/machinary") ||
    lower.includes("/machinery") ||
    lower.includes("/machine")
  )
    return "Machinary";
  return "All";
};

const CATEGORY_ORDER = [
  "Home",
  "About",
  "Contact",
  "Our Business",
  "Blog",
  "Machinary",
];

function App() {
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [activeTab, setActiveTab] = useState("All");
  const [filtered, setFiltered] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [error, setError] = useState(null);

  const fetchResults = useCallback(async (q) => {
    if (!q) return;
    const base =
      process.env.REACT_APP_SEARCH_API_URL ||
      "https://texcoms.azure-api.net/search";
    const key = process.env.REACT_APP_SEARCH_API_KEY || "";
    try {
      setLoading(true);
      setError(null);
      const url = `${base}?q=${encodeURIComponent(q)}${
        key ? `&Key=${encodeURIComponent(key)}` : ""
      }`;
      const resp = await fetch(url, {
        headers: { Accept: "application/json" },
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${txt.slice(0, 120)}`);
      }
      const ct = resp.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        const txt = await resp.text();
        throw new Error("Non-JSON response: " + txt.slice(0, 120));
      }
      const data = await resp.json();
      const raw = Array.isArray(data)
        ? data
        : Array.isArray(data.results)
        ? data.results
        : [];
      const withCat = raw.map((r) => ({ ...r, category: categorize(r.url) }));
      setResults(withCat);
    } catch (e) {
      setResults([]);
      setAvailableCategories([]);
      setError(e.message);
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  }, []);

  // Debounce search
  useEffect(() => {
    if (!overlayOpen || !searchTerm.trim()) {
      setResults([]);
      setAvailableCategories([]);
      setHasFetched(false);
      setError(null);
      return;
    }
    const t = setTimeout(() => fetchResults(searchTerm.trim()), 400);
    return () => clearTimeout(t);
  }, [searchTerm, overlayOpen, fetchResults]);

  // Filter on tab
  useEffect(() => {
    if (activeTab === "All") setFiltered(results);
    else setFiltered(results.filter((r) => r.category === activeTab));
  }, [results, activeTab]);

  // Recompute categories
  useEffect(() => {
    const counts = results.reduce((a, r) => {
      if (r.category) a[r.category] = (a[r.category] || 0) + 1;
      return a;
    }, {});
    const cats = CATEGORY_ORDER.filter((c) => counts[c] > 0);
    setAvailableCategories(cats);
    if (activeTab !== "All" && !cats.includes(activeTab)) setActiveTab("All");
  }, [results, activeTab]);

  useEffect(() => {
    try {
      window.parent?.postMessage({ type: "TEX_SEARCH_READY" }, "*");
      // Send initial collapsed dimensions
      window.parent?.postMessage(
        { type: "TEX_SEARCH_COLLAPSED", width: 170, height: 56 },
        "*"
      );
    } catch {}
    document.body.classList.add("tex-search-ready");
  }, []);

  // Resize / state notify (open vs collapsed)
  useEffect(() => {
    if (overlayOpen) {
      setTimeout(() => {
        try {
          window.parent.postMessage({ type: "TEX_SEARCH_OPEN" }, "*");
          window.parent.postMessage({ type: "TEX_SEARCH_EXPANDED" }, "*");
        } catch (e) {
          console.log("PostMessage failed", e);
        }
      }, 300); // delay allows iframe & DUDA DOM to be ready
    } else {
      try {
        window.parent.postMessage({ type: "TEX_SEARCH_CLOSE" }, "*");
        window.parent.postMessage(
          { type: "TEX_SEARCH_COLLAPSED", width: 170, height: 56 },
          "*"
        );
      } catch {}
    }
  }, [overlayOpen]);

  // Height notify (embed) – keep existing but now honors forced height
  useEffect(() => {
    const notify = () => {
      try {
        window.parent?.postMessage(
          {
            type: "TEXCOMS_SEARCH_HEIGHT",
            height: document.documentElement.scrollHeight,
          },
          "*"
        );
      } catch {}
    };
    notify();
    const ro = new ResizeObserver(() => notify());
    ro.observe(document.body);
    return () => ro.disconnect();
  }, [overlayOpen, filtered, availableCategories]);

  useEffect(() => {
    const applyViewportClasses = () => {
      const isMobile = window.innerWidth <= 1024;
      document.body.classList.toggle("tex-mobile", isMobile);
      document.body.classList.toggle("tex-desktop", !isMobile);
    };
    applyViewportClasses();
    window.addEventListener("resize", applyViewportClasses);
    window.addEventListener("orientationchange", applyViewportClasses);
    return () => {
      window.removeEventListener("resize", applyViewportClasses);
      window.removeEventListener("orientationchange", applyViewportClasses);
    };
  }, []);
  return (
    <>
      {/* 1️⃣ Overlay FIRST so it can expand full page */}
      {overlayOpen && (
        <SearchOverlay
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm} // <-- YOU MISSED THIS
          close={() => {
            document.body.classList.remove("tex-search-open");
            setOverlayOpen(false);
            setSearchTerm("");
            setResults([]);
            try {
              window.parent?.postMessage({ type: "TEX_SEARCH_CLOSE" }, "*");
            } catch {}
          }}
          loading={loading}
          error={error}
          hasFetched={hasFetched}
          results={filtered}
          allResults={results}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          categories={availableCategories}
        >
          <Results
            results={filtered}
            loading={loading}
            error={error}
            defaultImage="/texcoms-logo.png"
            hasQuery={!!searchTerm.trim()}
            hasFetched={hasFetched}
            query={searchTerm}
            onResultClick={() => setOverlayOpen(false)}
          />
        </SearchOverlay>
      )}

      {/* 2️⃣ Your site content wrapper BELOW (collapsed mode) */}
      <div className="flex-element group">
        <div className="app-container search-only">
          <SearchBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            isOpen={overlayOpen}
            openSearch={() => {
              setOverlayOpen(true);
              document.body.classList.add("tex-search-open");
              try {
                window.parent?.postMessage({ type: "TEX_SEARCH_OPEN" }, "*");
              } catch {}
            }}
          />
        </div>
      </div>
    </>
  );
}

export default App;
